/**
 * The arithmetic of the image pipeline, with no browser APIs, so it is unit
 * tested on its own: purpose presets, output sizes, the encode attempts
 * (quality steps first, then smaller dimensions) and the loop that tries
 * them until one fits, and what to do with an animation.
 */
import {
  chatImageBytes,
  IMAGE_LIMITS,
  MAX_ANIMATION_FRAMES,
  MAX_ANIMATION_PIXELS,
  type ImagePurpose,
} from '@neosocial/protocol';
import type { SourceInfo } from './sniff';

export interface Size {
  width: number;
  height: number;
}

/**
 * Canvas limits. 4096 x 4096 (16.7 megapixels) is the smallest area cap in
 * use (iOS Safari); 16384 the longest side most engines allow. No canvas
 * this pipeline makes is bigger.
 */
export const MAX_CANVAS_AREA = 4096 * 4096;
export const MAX_CANVAS_SIDE = 16384;

/** The crop editor zooms from "the whole image covers the mask" up to this. */
export const MAX_ZOOM = 4;

export type EncodeType = 'image/webp' | 'image/jpeg' | 'image/png';

export interface Preset {
  purpose: ImagePurpose;
  /** The output fits inside this box... */
  maxWidth: number;
  maxHeight: number;
  /** ...and has at most this many pixels (the server's `maxPixels`). */
  maxArea: number;
  /**
   * Big sources are shrunk to this many pixels, keeping their shape (by
   * area, not by the long edge, so a tall screenshot keeps its width). A
   * chat image already within the limits keeps its own size when it fits
   * the target at the first quality; otherwise it gets this area (2560 x
   * 2560 worth, 6.5 megapixels).
   */
  baseArea: number;
  /** The editor's crop aspect (width / height); null keeps the image's own shape (no editor). */
  aspect: number | null;
  /** The editor's mask. */
  mask: 'circle' | 'rounded' | 'rect' | null;
  /** Aim for this many bytes; the server's own limit is `maxBytes`. */
  targetBytes: number;
  maxBytes: number;
  /** A small enough animation is sent as it is (the server keeps it animated). */
  keepAnimation: boolean;
  /** Quality steps for WebP and JPEG, best first. */
  qualities: readonly number[];
  /** Dimensions never step below this long edge. */
  minEdge: number;
}

const L = IMAGE_LIMITS;

export const PRESETS: Readonly<Record<ImagePurpose, Readonly<Preset>>> = {
  avatar: {
    purpose: 'avatar',
    maxWidth: L.avatar.maxWidth,
    maxHeight: L.avatar.maxHeight,
    maxArea: L.avatar.maxPixels,
    baseArea: L.avatar.maxPixels,
    aspect: 1,
    mask: 'circle',
    targetBytes: L.avatar.targetBytes,
    maxBytes: L.avatar.maxBytes,
    keepAnimation: false,
    qualities: [0.9, 0.84, 0.76, 0.68, 0.6],
    minEdge: 256,
  },
  icon: {
    purpose: 'icon',
    maxWidth: L.icon.maxWidth,
    maxHeight: L.icon.maxHeight,
    maxArea: L.icon.maxPixels,
    baseArea: L.icon.maxPixels,
    aspect: 1,
    mask: 'rounded',
    targetBytes: L.icon.targetBytes,
    maxBytes: L.icon.maxBytes,
    keepAnimation: false,
    qualities: [0.9, 0.84, 0.76, 0.68, 0.6],
    minEdge: 256,
  },
  banner: {
    purpose: 'banner',
    maxWidth: L.banner.maxWidth,
    maxHeight: L.banner.maxHeight,
    maxArea: L.banner.maxPixels,
    baseArea: L.banner.maxPixels,
    aspect: L.banner.maxWidth / L.banner.maxHeight,
    mask: 'rect',
    targetBytes: L.banner.targetBytes,
    maxBytes: L.banner.maxBytes,
    keepAnimation: false,
    qualities: [0.86, 0.8, 0.72, 0.64],
    minEdge: 800,
  },
  // a scheduled event's cover (2026-10-29): 5:2 like a banner, at 800 x 320
  event_cover: {
    purpose: 'event_cover',
    maxWidth: L.event_cover.maxWidth,
    maxHeight: L.event_cover.maxHeight,
    maxArea: L.event_cover.maxPixels,
    baseArea: L.event_cover.maxPixels,
    aspect: L.event_cover.maxWidth / L.event_cover.maxHeight,
    mask: 'rect',
    targetBytes: L.event_cover.targetBytes,
    maxBytes: L.event_cover.maxBytes,
    keepAnimation: false,
    qualities: [0.84, 0.78, 0.7, 0.62],
    minEdge: 480,
  },
  emoji: {
    purpose: 'emoji',
    maxWidth: L.emoji.maxWidth,
    maxHeight: L.emoji.maxHeight,
    maxArea: L.emoji.maxPixels,
    baseArea: L.emoji.maxPixels,
    aspect: null,
    mask: null,
    targetBytes: L.emoji.targetBytes,
    maxBytes: L.emoji.maxBytes,
    keepAnimation: true,
    qualities: [0.9, 0.8, 0.7],
    minEdge: 64,
  },
  attachment: {
    purpose: 'attachment',
    maxWidth: L.attachment.maxWidth,
    maxHeight: L.attachment.maxHeight,
    maxArea: L.attachment.maxPixels,
    baseArea: 2560 * 2560,
    aspect: null,
    mask: null,
    targetBytes: L.attachment.targetBytes,
    maxBytes: L.attachment.maxBytes,
    keepAnimation: true,
    qualities: [0.86, 0.8, 0.72, 0.64, 0.56],
    minEdge: 640,
  },
};

/**
 * The preset for `purpose`. Chat images follow the console's upload limit
 * (`limits.upload_image_bytes`, from the live config): it is the most bytes
 * the server accepts for one, so the client shrinks to fit under it, aiming
 * at half of it (protocol `chatImageBytes`). Unknown (no config yet) keeps
 * the server's own limits.
 */
export function presetFor(purpose: ImagePurpose, uploadImageBytes?: number): Preset {
  const p = PRESETS[purpose];
  if (purpose !== 'attachment' || uploadImageBytes === undefined) return p;
  return { ...p, ...chatImageBytes(uploadImageBytes) };
}

const clamp1 = (n: number) => Math.max(1, Math.round(n));

/** Scales `src` down (never up) to fit inside `box`, keeping its shape. */
export function fitInside(src: Size, box: Size): Size {
  const s = Math.min(1, box.width / src.width, box.height / src.height);
  return { width: clamp1(src.width * s), height: clamp1(src.height * s) };
}

/** Scales `src` down so its long edge is at most `edge`. */
export function fitEdge(src: Size, edge: number): Size {
  return fitInside(src, { width: edge, height: edge });
}

/** Scales `src` down (never up) to at most `area` pixels, keeping its shape. */
export function fitArea(src: Size, area: number): Size {
  const px = src.width * src.height;
  if (px <= area) return src;
  const s = Math.sqrt(area / px);
  // floor, so rounding never lands just over the area
  return {
    width: Math.max(1, Math.floor(src.width * s)),
    height: Math.max(1, Math.floor(src.height * s)),
  };
}

/** Shrinks a size until a canvas of it is allowed everywhere. */
export function capCanvas(size: Size, maxArea = MAX_CANVAS_AREA, maxSide = MAX_CANVAS_SIDE): Size {
  let s = Math.min(1, maxSide / size.width, maxSide / size.height);
  const area = size.width * s * (size.height * s);
  if (area > maxArea) s *= Math.sqrt(maxArea / area);
  // floor, so rounding never lands just over the cap
  return {
    width: Math.max(1, Math.floor(size.width * s)),
    height: Math.max(1, Math.floor(size.height * s)),
  };
}

/** The box every output fits inside. */
function box(preset: Preset): Size {
  return { width: preset.maxWidth, height: preset.maxHeight };
}

/**
 * The dimensions to try, largest first. `src` is the upright source size
 * (the crop's size, for an edited image). Step 0 may be a "bonus" size
 * bigger than the base area: a source already within the limits (a chat
 * image up to 16.7 megapixels, a 1080 x 9000 screenshot) is tried once as
 * it is.
 */
export function sizeLadder(preset: Preset, src: Size): { sizes: Size[]; bonus: boolean } {
  if (!(src.width >= 1 && src.height >= 1)) throw new RangeError('image has no size');
  const inBox = capCanvas(fitArea(fitInside(src, box(preset)), preset.maxArea));
  const base = capCanvas(fitArea(fitInside(src, box(preset)), preset.baseArea));
  const sizes: Size[] = [];
  const within =
    src.width <= preset.maxWidth &&
    src.height <= preset.maxHeight &&
    src.width * src.height <= preset.maxArea;
  const bonus = within && inBox.width > base.width;
  if (bonus) sizes.push(inBox);
  sizes.push(base);
  let cur = base;
  for (let step = 0; step < 24; step++) {
    const long = Math.max(cur.width, cur.height);
    if (long <= preset.minEdge) break;
    const next = fitEdge(cur, Math.max(preset.minEdge, Math.round(long * 0.8)));
    if (next.width >= cur.width && next.height >= cur.height) break;
    sizes.push(next);
    cur = next;
  }
  return { sizes, bonus };
}

export interface EncodeAttempt extends Size {
  type: EncodeType;
  /** 0..1 for WebP and JPEG; undefined for PNG. */
  quality?: number;
}

/**
 * Every encode to try, in order: the bonus size once at the best quality,
 * the base size at every quality step, then each smaller size at the two
 * lowest steps. `webp` says whether this browser can encode WebP (alpha is
 * kept); without it, an image with transparency goes PNG and anything else
 * JPEG.
 */
export function encodeAttempts(
  preset: Preset,
  src: Size,
  o: { webp: boolean; alpha: boolean },
): EncodeAttempt[] {
  const { sizes, bonus } = sizeLadder(preset, src);
  const type: EncodeType = o.webp ? 'image/webp' : o.alpha ? 'image/png' : 'image/jpeg';
  const out: EncodeAttempt[] = [];
  const q = preset.qualities;
  sizes.forEach((s, i) => {
    if (type === 'image/png') {
      out.push({ ...s, type });
      return;
    }
    const base = bonus ? 1 : 0;
    const steps = i < base ? q.slice(0, 1) : i === base ? q : q.slice(-2);
    for (const quality of steps) out.push({ ...s, type, quality });
  });
  return out;
}

/** Tiny images, and flat-colour ones (logos, screenshots of UI), also try lossless PNG. */
export function wantsPng(size: Size, traits: { flat: boolean }): boolean {
  return traits.flat || size.width * size.height <= 256 * 256;
}

export class ImageTooLargeError extends Error {
  constructor() {
    super('image does not fit the size limit');
    this.name = 'ImageTooLargeError';
  }
}

export interface Encoded {
  blob: Blob;
  attempt: EncodeAttempt;
  /** Encodes run. */
  tries: number;
}

/**
 * Runs the attempts in order and returns the first result within
 * `targetBytes`. When a PNG attempt is given (tiny or flat images), it runs
 * first, against the first lossy attempt: the smaller of the two wins if it
 * fits. When nothing reaches the target, the smallest result within
 * `maxBytes` is used; past that, ImageTooLargeError.
 */
export async function encodeToFit(
  attempts: EncodeAttempt[],
  encode: (a: EncodeAttempt) => Promise<Blob>,
  limits: { targetBytes: number; maxBytes: number },
  o: {
    png?: EncodeAttempt;
    onStep?: (done: number, total: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<Encoded> {
  let tries = 0;
  let best: Encoded | null = null;
  const total = attempts.length + (o.png ? 1 : 0);
  const run = async (a: EncodeAttempt): Promise<Encoded> => {
    if (o.signal?.aborted) throw o.signal.reason ?? new DOMException('Aborted', 'AbortError');
    const blob = await encode(a);
    tries += 1;
    o.onStep?.(tries, total);
    const r = { blob, attempt: a, tries };
    if (!best || blob.size < best.blob.size) best = r;
    return r;
  };
  let pngResult: Encoded | null = null;
  if (o.png) pngResult = await run(o.png);
  for (const [i, a] of attempts.entries()) {
    const r = await run(a);
    if (i === 0 && pngResult && pngResult.blob.size <= limits.targetBytes) {
      const win = pngResult.blob.size <= r.blob.size ? pngResult : r;
      return { ...win, tries };
    }
    if (r.blob.size <= limits.targetBytes) return { ...r, tries };
  }
  const b = best as Encoded | null;
  if (b && b.blob.size <= limits.maxBytes) return { ...b, tries };
  throw new ImageTooLargeError();
}

/** What happens to an animated source. */
export type AnimationPlan =
  | { kind: 'encode' }
  | { kind: 'passthrough' }
  | { kind: 'still'; reason: 'stillOnly' | 'tooLarge' | 'apng' };

/**
 * A still image is encoded. An animated GIF or WebP for a purpose that
 * keeps animation (chat images) is sent as it is when the server will take
 * it (bytes, pixels per frame, frames, pixels in all); otherwise, and for
 * avatars, icons and banners, the first frame is used and the user is told.
 * Animated PNG is always a still (the server keeps no APNG animation).
 */
export function animationPlan(preset: Preset, info: SourceInfo, bytes: number): AnimationPlan {
  if (!info.animated) return { kind: 'encode' };
  if (!preset.keepAnimation) return { kind: 'still', reason: 'stillOnly' };
  if (info.kind === 'png') return { kind: 'still', reason: 'apng' };
  const lim = IMAGE_LIMITS[preset.purpose];
  const px = (info.width ?? Infinity) * (info.height ?? Infinity);
  const fits =
    (info.kind === 'gif' || info.kind === 'webp') &&
    bytes <= preset.maxBytes &&
    px <= lim.maxInputPixels &&
    info.frames <= MAX_ANIMATION_FRAMES &&
    px * info.frames <= MAX_ANIMATION_PIXELS;
  return fits ? { kind: 'passthrough' } : { kind: 'still', reason: 'tooLarge' };
}

/**
 * The size to decode an image at for the crop editor: big enough that the
 * editor's highest zoom still has a pixel for every output pixel (the
 * short side gets 512 x MAX_ZOOM for an avatar), never bigger than the
 * source or a safe canvas (16.7 megapixels, 16384 a side). The cap is by
 * area, so a wide panorama keeps its short side: any source up to 32:1
 * still gives a full 512 px avatar at zoom 1. Only a long, thin source
 * (over about 4:1) zoomed far in has fewer pixels than that, because a
 * safe canvas cannot hold it all at full size.
 */
export function workingSize(preset: Preset, src: Size): Size {
  const need = { width: preset.maxWidth * MAX_ZOOM, height: preset.maxHeight * MAX_ZOOM };
  const s = Math.min(1, Math.max(need.width / src.width, need.height / src.height));
  return capCanvas({ width: clamp1(src.width * s), height: clamp1(src.height * s) });
}

/** The decode size for an image that is encoded without the editor: the first size to try. */
export function decodeSize(preset: Preset, src: Size): Size {
  return sizeLadder(preset, src).sizes[0]!;
}

/** The new file name: same base, extension of the type written. */
export function renameFor(name: string, type: string): string {
  const ext =
    type === 'image/webp'
      ? 'webp'
      : type === 'image/jpeg'
        ? 'jpg'
        : type === 'image/png'
          ? 'png'
          : type === 'image/gif'
            ? 'gif'
            : 'img';
  const base = name.replace(/\.[^./\\]*$/, '') || 'image';
  return `${base}.${ext}`;
}
